import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { formatDisplayDate } from "../utils/dateFormat";
import PaginationFooter from "../components/PaginationFooter";
import AppTooltip from "../components/AppTooltip";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as cashLedgerApi from "../api/cashLedger";

const emptyForm = {
  registrationId: "",
  amount: "",
  paymentMode: "cash",
  receivedDate: "",
  serialNumber: "",
  notes: "",
};

const filterControlStyle = {
  width: "165px",
  height: "38px",
  border: "none",
  borderRadius: "8px",
  backgroundColor: "#fff",
  fontSize: "12px",
};

const inputStyle = {
  border: "1px solid #e5e1ef",
  borderRadius: "9px",
  padding: "10px 13px",
  color: "#403b55",
  backgroundColor: "#fff",
};

const labelStyle = {
  fontSize: "13px",
  fontWeight: "600",
  color: "#403b55",
};

export default function AdminCashLedger() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_cash_ledger");
  const { hasPermission: canEdit } = useViewPermission("edit_cash_ledger");

  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;

  const [filters, setFilters] = useState({
    registrationId: "",
    paymentMode: "",
    entryType: "",
    fromDate: "",
    toDate: "",
  });
  const debouncedRegistrationId = useDebouncedValue(
    filters.registrationId,
    400,
  );

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const todayDate = () => new Date().toISOString().slice(0, 10);

  const isTodayFilterActive =
    filters.fromDate === todayDate() &&
    filters.toDate === todayDate() &&
    !filters.registrationId &&
    !filters.paymentMode &&
    !filters.entryType;

  const fetchEntries = async (
    overrideFilters = filters,
    isInitial = false,
    pageArg = currentPage,
  ) => {
    if (!isInitial) setRefreshing(true);
    try {
      const response = await cashLedgerApi.list({
        page: pageArg,
        limit,
        filters: {
          registrationId: String(
            overrideFilters.registrationId ?? "",
          ).trim(),
          paymentMode: overrideFilters.paymentMode,
          fromDate: overrideFilters.fromDate,
          toDate: overrideFilters.toDate,
        },
      });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setEntries(normalized.data);
      setCurrentPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching cash ledger entries:", err);
      notificationCtx.show({
        message: "Failed to load cash ledger entries.",
        stat: "error",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const updateFilters = (patch, { refetch = true } = {}) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setCurrentPage(1);
    if (refetch) fetchEntries(next, false, 1);
  };

  useEffect(() => {
    if (!hasPermission) return;
    fetchEntries(filters, true, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  useEffect(() => {
    if (!hasPermission || loading) return;
    setCurrentPage(1);
    fetchEntries(
      { ...filters, registrationId: debouncedRegistrationId },
      false,
      1,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRegistrationId]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchEntries(filters, false, newPage);
    }
  };

  const openAddForm = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      receivedDate: new Date().toISOString().slice(0, 10),
    });
    setShowForm(true);
  };

  const reversedOriginalIds = new Set(
    entries
      .filter(
        (e) =>
          e.isReversed ||
          (e.entryType === "reversal" && e.reversesEntryId != null),
      )
      .flatMap((e) =>
        e.isReversed ? [Number(e.id)] : [Number(e.reversesEntryId)],
      ),
  );

  const canMutateEntry = (entry) =>
    entry?.entryType === "receipt" &&
    !entry.isReversed &&
    !reversedOriginalIds.has(Number(entry.id));

  const netTotal = entries.reduce((sum, entry) => {
    const amount = Number(entry.amount) || 0;
    if (entry.entryType === "reversal" || entry.entryType === "refund") {
      return sum - amount;
    }
    return sum + amount;
  }, 0);

  const formatAmountSigned = (entry) => {
    const amount = Number(entry.amount) || 0;
    const formatted = amount.toLocaleString();
    return entry.entryType === "reversal" || entry.entryType === "refund"
      ? `−${formatted}`
      : formatted;
  };

  const entryStatusLabel = (entry) => {
    if (entry.entryType === "reversal") return "Reversal";
    if (entry.entryType === "refund") return "Refund";
    if (entry.isReversed || reversedOriginalIds.has(Number(entry.id))) {
      return "Reversed";
    }
    if (entry.replacesEntryId) return "Replacement";
    return "Receipt";
  };

  const openEditForm = (entry) => {
    if (!canMutateEntry(entry)) return;
    setEditingId(entry.id);
    setForm({
      registrationId: String(entry.registrationId ?? ""),
      amount: String(entry.amount ?? ""),
      paymentMode: entry.paymentMode || "cash",
      receivedDate: entry.receivedDate || "",
      serialNumber: entry.serialNumber || "",
      notes: entry.notes || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    if (saving) return;

    const payload = {
      registrationId: Number(form.registrationId),
      amount: Number(form.amount),
      paymentMode: form.paymentMode,
      receivedDate: form.receivedDate,
      serialNumber: form.serialNumber.trim() || null,
      notes: form.notes.trim() || null,
    };

    if (
      !payload.registrationId ||
      !payload.amount ||
      !payload.paymentMode ||
      !payload.receivedDate
    ) {
      notificationCtx.show({
        message: "Please fill registration ID, amount, payment mode, and date.",
        stat: "error",
      });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await cashLedgerApi.softEdit(editingId, payload);
        notificationCtx.show({
          message: "Entry updated via reversal + replacement.",
          stat: "success",
        });
      } else {
        await cashLedgerApi.create(payload);
        notificationCtx.show({
          message: "Cash ledger entry added successfully!",
          stat: "success",
        });
      }
      closeForm();
      await fetchEntries();
    } catch (error) {
      console.error("Error saving cash ledger entry:", error);
      notificationCtx.show({
        message:
          error.response?.data?.message || "Failed to save cash ledger entry.",
        stat: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReverse = (entry) => {
    if (!canEdit) return;
    if (!canMutateEntry(entry)) return;

    const confirmReverse = async () => {
      try {
        await cashLedgerApi.reverse(entry.id);
        notificationCtx.show({
          message: "Cash ledger entry reversed successfully",
          stat: "success",
        });
        await fetchEntries();
      } catch (error) {
        console.error("Reverse failed:", error);
        notificationCtx.show({
          message:
            error.response?.data?.message ||
            "Failed to reverse cash ledger entry",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message:
        "This will create a reversal entry against this record (not permanently delete). Continue?",
      stat: "info",
      onConfirm: confirmReverse,
    });
  };

  const formatMode = (mode) =>
    mode === "online" ? "Online" : mode === "cash" ? "Cash" : mode;

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            height: "109vh",
            minHeight: 0,
            margin: "100px 50px 0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              backgroundColor: "#d5d1fd",
              borderRadius: "20px",
              padding: "26px 30px",
              flexShrink: 0,
              zIndex: 10,
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center"
              style={{ marginBottom: "16px" }}
            >
              <div>
                <h1
                  className="mb-1"
                  style={{ fontWeight: "700", color: "#302d45" }}
                >
                  CASH LEDGER
                </h1>
                <small style={{ color: "#625d75" }}>
                  Record reception incomings by registration, mode, amount,
                  date, and voucher serial. Edits and deletes create reversals.
                </small>
              </div>

              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn"
                  disabled={refreshing}
                  onClick={() => {
                    const cleared = {
                      registrationId: "",
                      paymentMode: "",
                      fromDate: "",
                      toDate: "",
                    };
                    setFilters(cleared);
                    setCurrentPage(1);
                    fetchEntries(cleared, false, 1);
                  }}
                  style={{
                    height: "38px",
                    backgroundColor: "#fff",
                    border: "none",
                    color: "#625d75",
                    fontWeight: "600",
                    fontSize: "13px",
                    padding: "0 15px",
                    borderRadius: "8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Reset All
                </button>
                {canEdit && (
                  <button
                    className="btn"
                    onClick={openAddForm}
                    style={{
                      height: "38px",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#fff",
                      color: "#403b55",
                      border: "none",
                      padding: "0 15px",
                      borderRadius: "8px",
                      fontWeight: "600",
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    + Add Entry
                  </button>
                )}
                <div
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    padding: "8px 14px",
                    minWidth: "140px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "700",
                      color: "#8a8498",
                      letterSpacing: "0.4px",
                      textTransform: "uppercase",
                    }}
                  >
                    Net total
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: netTotal >= 0 ? "#2f6b4f" : "#8a3b3b",
                    }}
                  >
                    PKR {netTotal.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <input
                  type="number"
                  className="form-control"
                  placeholder="Registration ID"
                  value={filters.registrationId}
                  onChange={(e) =>
                    updateFilters(
                      { registrationId: e.target.value },
                      { refetch: false },
                    )
                  }
                  style={filterControlStyle}
                />
                <select
                  className="form-select"
                  value={filters.paymentMode}
                  onChange={(e) =>
                    updateFilters({ paymentMode: e.target.value })
                  }
                  style={filterControlStyle}
                >
                  <option value="">All modes</option>
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                </select>
                <select
                  className="form-select"
                  value={filters.entryType}
                  onChange={(e) =>
                    updateFilters({ entryType: e.target.value })
                  }
                  style={filterControlStyle}
                >
                  <option value="">All types</option>
                  <option value="receipt">Receipt</option>
                  <option value="reversal">Reversal</option>
                  <option value="refund">Refund</option>
                </select>
                <button
                  className="btn"
                  disabled={refreshing}
                  onClick={() => {
                    if (isTodayFilterActive) {
                      const cleared = {
                        registrationId: "",
                        paymentMode: "",
                        entryType: "",
                        fromDate: "",
                        toDate: "",
                      };
                      setFilters(cleared);
                      setCurrentPage(1);
                      fetchEntries(cleared, false, 1);
                      return;
                    }

                    const today = todayDate();
                    const todayOnly = {
                      registrationId: "",
                      paymentMode: "",
                      entryType: "",
                      fromDate: today,
                      toDate: today,
                    };
                    setFilters(todayOnly);
                    setCurrentPage(1);
                    fetchEntries(todayOnly, false, 1);
                  }}
                  style={{
                    height: "38px",
                    backgroundColor: isTodayFilterActive ? "#bff1db" : "#fff",
                    border: isTodayFilterActive
                      ? "1px solid #9fd9bf"
                      : "1px solid #e5e1ef",
                    color: "#403b55",
                    fontWeight: "600",
                    fontSize: "13px",
                    padding: "0 14px",
                    borderRadius: "8px",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      backgroundColor: isTodayFilterActive
                        ? "#355c4a"
                        : "#aaa5b3",
                      flexShrink: 0,
                    }}
                  />
                  {isTodayFilterActive
                    ? "Showing Today's Collections"
                    : "Show Today's Collections"}
                </button>
              </div>

              <div>
                <label
                  className="form-label mb-1"
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#514b65",
                    letterSpacing: "0.2px",
                  }}
                >
                  RECEIVED DATE RANGE
                </label>

                <div className="d-flex align-items-center gap-2">
                  <input
                    className="form-control"
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        fromDate: e.target.value,
                      }))
                    }
                    style={filterControlStyle}
                  />

                  <span style={{ color: "#77718b", fontSize: "12px" }}>to</span>

                  <input
                    className="form-control"
                    type="date"
                    value={filters.toDate}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        toDate: e.target.value,
                      }))
                    }
                    style={filterControlStyle}
                  />

                  <button
                    className="btn"
                    disabled={
                      refreshing || !filters.fromDate || !filters.toDate
                    }
                    onClick={() => {
                      if (
                        new Date(filters.fromDate) > new Date(filters.toDate)
                      ) {
                        notificationCtx.show({
                          message: "Start date cannot be after end date.",
                        });
                        return;
                      }
                      setCurrentPage(1);
                      fetchEntries(filters, false, 1);
                    }}
                    style={{
                      height: "38px",
                      backgroundColor: "#fff",
                      color: "#514637",
                      border: "none",
                      fontWeight: "600",
                      fontSize: "12px",
                      padding: "0 15px",
                      borderRadius: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Apply Date Filter
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: "16px", flexShrink: 0 }} />

          {entries.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                flex: 1,
                minHeight: 0,
                backgroundColor: "#fff",
                borderRadius: "14px",
                border: "1px solid #eeeaf8",
                boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                padding: "60px 20px",
                color: "#77718b",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#403b55",
                }}
              >
                No cash ledger entries
              </div>
              <small style={{ marginTop: "5px" }}>
                Record an incoming payment to see it here.
              </small>
            </div>
          ) : (
            <>
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  borderRadius: "14px",
                  border: "1px solid #eeeaf8",
                  boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                }}
              >
                <div style={{ height: "100%", overflow: "auto" }}>
                  <table
                    className="table align-middle mb-0"
                    style={{
                      minWidth: "1180px",
                      width: "100%",
                      background: "#fff",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                    }}
                  >
                    <thead>
                      <tr>
                        {[
                          "DATE",
                          "TYPE",
                          "REG ID",
                          "STUDENT",
                          "EMAIL",
                          "AMOUNT",
                          "MODE",
                          "SERIAL NO",
                          "NOTES",
                          "ACTIONS",
                        ].map((heading) => (
                          <th
                            key={heading}
                            style={{
                              backgroundColor: "#d5d1fd",
                              color: "#403b55",
                              fontWeight: "600",
                              padding: "14px 16px",
                              textAlign: "center",
                              letterSpacing: "0.4px",
                              fontSize: "13px",
                              borderBottom: "1px solid #e6e3eb",
                              whiteSpace: "nowrap",
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                            }}
                          >
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => {
                        const isReversal = entry.entryType === "reversal";
                        const isRefund = entry.entryType === "refund";
                        const isReversed =
                          !isReversal &&
                          !isRefund &&
                          reversedOriginalIds.has(Number(entry.id));
                        const mutable = canMutateEntry(entry);
                        const status = entryStatusLabel(entry);
                        const rowMuted = isReversal || isRefund || isReversed;

                        return (
                          <tr key={entry.id}>
                            <td
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                fontSize: "14px",
                                color: rowMuted ? "#8a8498" : "#403b55",
                                borderBottom: "1px solid #f0eef4",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDisplayDate(entry.receivedDate)}
                            </td>
                            <td
                              style={{
                                backgroundColor: "#fff",
                                padding: "13px 16px",
                                textAlign: "center",
                                borderBottom: "1px solid #f0eef4",
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "4px 8px",
                                  borderRadius: "7px",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  backgroundColor: isReversal || isRefund
                                    ? "#fff0f0"
                                    : isReversed
                                      ? "#f3f1f7"
                                      : entry.replacesEntryId
                                        ? "#eef6ff"
                                        : "#eefaf4",
                                  color: isReversal || isRefund
                                    ? "#8a3b3b"
                                    : isReversed
                                      ? "#625d75"
                                      : entry.replacesEntryId
                                        ? "#355c7a"
                                        : "#355c4a",
                                }}
                              >
                                {status}
                              </span>
                            </td>
                            {[
                              entry.registrationId,
                              entry.Registration?.name || "—",
                              entry.Registration?.email || "—",
                              formatAmountSigned(entry),
                              formatMode(entry.paymentMode),
                              entry.serialNumber || "—",
                              entry.notes || "—",
                            ].map((value, idx) => (
                              <td
                                key={`${entry.id}-${idx}`}
                                style={{
                                  backgroundColor: "#fff",
                                  padding: "13px 16px",
                                  textAlign: "center",
                                  fontSize: "14px",
                                  color:
                                    idx === 3 && (isReversal || isRefund)
                                      ? "#8a3b3b"
                                      : rowMuted
                                        ? "#8a8498"
                                        : "#403b55",
                                  fontWeight: idx === 3 ? "600" : undefined,
                                  borderBottom: "1px solid #f0eef4",
                                  whiteSpace: "nowrap",
                                  verticalAlign: "middle",
                                  maxWidth: idx === 6 ? "220px" : undefined,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  textDecoration: isReversed
                                    ? "line-through"
                                    : undefined,
                                }}
                              >
                                <AppTooltip
                                  label={
                                    typeof value === "string" ? value : ""
                                  }
                                  wrap
                                  className="app-tip--stretch"
                                  disabled={typeof value !== "string"}
                                >
                                  <span
                                    style={{
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {value}
                                  </span>
                                </AppTooltip>
                              </td>
                            ))}
                            <td
                              style={{
                                textAlign: "center",
                                padding: "10px 16px",
                                borderBottom: "1px solid #f0eef4",
                              }}
                            >
                              {canEdit && mutable ? (
                                <div className="d-flex justify-content-center gap-2">
                                  <AppTooltip label="Edit (reversal + new entry)">
                                  <button
                                    className="btn"
                                    onClick={() => openEditForm(entry)}
                                    style={{
                                      backgroundColor: "#bff1db",
                                      border: "none",
                                      borderRadius: "8px",
                                      width: "38px",
                                      height: "34px",
                                      padding: 0,
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-pen"
                                      style={{
                                        fontSize: "14px",
                                        color: "#355c4a",
                                      }}
                                    />
                                  </button>
                                  </AppTooltip>
                                  <AppTooltip label="Reverse entry">
                                  <button
                                    className="btn"
                                    onClick={() => handleReverse(entry)}
                                    style={{
                                      backgroundColor: "#f2c4c4",
                                      border: "none",
                                      borderRadius: "8px",
                                      width: "38px",
                                      height: "34px",
                                      padding: 0,
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-trash"
                                      style={{
                                        fontSize: "14px",
                                        color: "#654343",
                                      }}
                                    />
                                  </button>
                                  </AppTooltip>
                                </div>
                              ) : (
                                <span style={{ color: "#aaa5b5" }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <PaginationFooter
                page={currentPage}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemLabel="entries"
                disabled={refreshing}
              />
            </>
          )}

          {showForm && (
            <div
              className="modal show"
              style={{
                display: "block",
                backgroundColor: "rgba(48,45,69,0.45)",
              }}
              tabIndex="-1"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                style={{ maxWidth: "520px" }}
              >
                <div
                  className="modal-content"
                  style={{
                    border: "none",
                    borderRadius: "18px",
                    overflow: "hidden",
                    boxShadow: "0 10px 35px rgba(48,45,69,0.18)",
                  }}
                >
                  <div
                    style={{
                      padding: "22px 24px",
                      backgroundColor: "#d5d1fd",
                      borderBottom: "1px solid rgba(80,70,110,0.08)",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4
                          className="mb-1"
                          style={{ fontWeight: "700", color: "#302d45" }}
                        >
                          {editingId ? "Edit Entry" : "Add Entry"}
                        </h4>
                        <small style={{ color: "#625d75" }}>
                          {editingId
                            ? "Saves as a reversal of the original plus a new entry"
                            : "Log a reception payment against a registration"}
                        </small>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={closeForm}
                        aria-label="Close"
                      />
                    </div>
                  </div>

                  <div style={{ padding: "24px", backgroundColor: "#fff" }}>
                    <form onSubmit={handleSubmit}>
                      <div className="mb-3">
                        <label className="form-label" style={labelStyle}>
                          Registration ID
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          required
                          min="1"
                          value={form.registrationId}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              registrationId: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label" style={labelStyle}>
                          Amount
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          required
                          min="0.01"
                          step="0.01"
                          value={form.amount}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              amount: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label" style={labelStyle}>
                          Payment Mode
                        </label>
                        <select
                          className="form-select"
                          required
                          value={form.paymentMode}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              paymentMode: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        >
                          <option value="cash">Cash</option>
                          <option value="online">Online</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label" style={labelStyle}>
                          Received Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          required
                          value={form.receivedDate}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              receivedDate: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label" style={labelStyle}>
                          Serial Number
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Voucher number"
                          value={form.serialNumber}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              serialNumber: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </div>

                      <div className="mb-4">
                        <label className="form-label" style={labelStyle}>
                          Notes (optional)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.notes}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </div>

                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn"
                          onClick={closeForm}
                          style={{
                            backgroundColor: "#f3f1f7",
                            border: "none",
                            color: "#625d75",
                            fontWeight: "600",
                            padding: "9px 18px",
                            borderRadius: "8px",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn"
                          disabled={saving}
                          style={{
                            backgroundColor: saving ? "#f3f1f7" : "#f2c4c4",
                            border: "none",
                            color: saving ? "#aaa5b5" : "#654343",
                            fontWeight: "600",
                            padding: "9px 20px",
                            borderRadius: "8px",
                          }}
                        >
                          {saving
                            ? "Saving..."
                            : editingId
                              ? "Save Edit"
                              : "Add Entry"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
